const MAX_ARRAY_ITEMS = 10;
const MAX_OBJECT_KEYS = 30;
const MAX_STRING_LENGTH = 2000;
const MAX_DEPTH = 4;
const UNSERIALIZABLE_OBJECT = "[Unserializable Object]";

const SENSITIVE_KEY_REGEX = /authorization|cookie|credential|password|secret|session|token/i;

type Primitive = string | number | boolean | null;
type SerializableValue = Primitive | SerializableValue[] | { [key: string]: SerializableValue };
type SerializableRecord = Record<string, SerializableValue>;

type SentryTags = Record<string, string | number | boolean | undefined>;
type ApolloErrorContext = SerializableRecord;

const isRecord = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === "object";

const getStringField = (record: Record<string, unknown>, key: string): string | undefined => {
    const value = record[key];
    return typeof value === "string" ? value : undefined;
};

const getArrayField = (record: Record<string, unknown>, key: string): unknown[] => {
    const value = record[key];
    return Array.isArray(value) ? value : [];
};

const truncate = (value: string): string =>
    value.length > MAX_STRING_LENGTH ? `${value.slice(0, MAX_STRING_LENGTH)}...` : value;

const getEnumerableEntries = (value: object): [string, unknown][] | null => {
    try {
        return Object.entries(value);
    } catch {
        return null;
    }
};

const sanitizeValue = (value: unknown, depth = 0, seen: WeakSet<object> = new WeakSet()): SerializableValue => {
    if (value === null || typeof value === "number" || typeof value === "boolean") {
        return value;
    }

    if (typeof value === "string") {
        return truncate(value);
    }

    if (typeof value === "bigint" || typeof value === "symbol" || typeof value === "function") {
        return String(value);
    }

    if (value === undefined) {
        return null;
    }

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? "Invalid Date" : value.toISOString();
    }

    if (!isRecord(value)) {
        return String(value);
    }

    if (seen.has(value)) {
        return "[Circular]";
    }

    if (depth >= MAX_DEPTH) {
        return "[Object]";
    }

    // Track on the way down and untrack on the way back up so `seen` is a path
    // set, not a global visited set: real cycles are still caught, but the same
    // object reached twice via different paths is not mislabeled "[Circular]".
    seen.add(value);
    try {
        if (value instanceof Error) {
            return serializeError(value, depth, seen);
        }

        if (Array.isArray(value)) {
            const items = value.slice(0, MAX_ARRAY_ITEMS).map((item) => sanitizeValue(item, depth + 1, seen));
            if (value.length > MAX_ARRAY_ITEMS) {
                items.push(`[${value.length - MAX_ARRAY_ITEMS} more item(s)]`);
            }
            return items;
        }

        const entries = getEnumerableEntries(value);
        if (!entries) {
            return UNSERIALIZABLE_OBJECT;
        }

        const sanitized: SerializableRecord = {};
        for (const [key, entryValue] of entries.slice(0, MAX_OBJECT_KEYS)) {
            sanitized[key] = SENSITIVE_KEY_REGEX.test(key) ? "[Filtered]" : sanitizeValue(entryValue, depth + 1, seen);
        }
        if (entries.length > MAX_OBJECT_KEYS) {
            sanitized["__truncated__"] = `${entries.length - MAX_OBJECT_KEYS} more key(s)`;
        }

        return sanitized;
    } finally {
        seen.delete(value);
    }
};

const addKnownErrorFields = (
    source: Record<string, unknown>,
    target: SerializableRecord,
    depth: number,
    seen: WeakSet<object>,
): void => {
    for (const key of ["code", "status", "statusCode", "bodyText"] as const) {
        const value = source[key];
        if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
            target[key] = value;
        }
    }

    const response = source["response"];
    if (isRecord(response) && typeof response["status"] === "number") {
        target["responseStatus"] = response["status"];
    }

    if ("result" in source) {
        target["result"] = sanitizeValue(source["result"], depth + 1, seen);
    }
};

const serializeError = (error: Error, depth: number, seen: WeakSet<object>): SerializableRecord => {
    const serialized: SerializableRecord = {
        name: error.name,
        message: error.message,
    };

    if (isRecord(error)) {
        addKnownErrorFields(error, serialized, depth, seen);
    }

    if (depth < MAX_DEPTH) {
        const entries = getEnumerableEntries(error);
        if (!entries) {
            serialized["properties"] = UNSERIALIZABLE_OBJECT;
            return serialized;
        }

        for (const [key, value] of entries) {
            // Skip `stack` and any field already captured by addKnownErrorFields
            // (e.g. `result`) so the generic pass does not re-sanitize and clobber it.
            // hasOwnProperty, not `in`, so inherited names (toString, valueOf, ...) are not skipped.
            if (key === "stack" || Object.prototype.hasOwnProperty.call(serialized, key)) {
                continue;
            }
            serialized[key] = SENSITIVE_KEY_REGEX.test(key) ? "[Filtered]" : sanitizeValue(value, depth + 1, seen);
        }
    }

    return serialized;
};

const serializeErrorLike = (value: unknown): SerializableRecord => {
    if (isRecord(value)) {
        const serialized = sanitizeValue(value);
        return isRecord(serialized) ? serialized : { message: String(serialized) };
    }

    return { message: String(value) };
};

const serializeGraphQLError = (value: unknown): SerializableRecord => {
    if (!isRecord(value)) {
        return { message: String(value) };
    }

    const serialized: SerializableRecord = {
        message: getStringField(value, "message") ?? String(value),
    };

    const path = value["path"];
    if (Array.isArray(path)) {
        serialized["path"] = sanitizeValue(path);
    }

    const locations = value["locations"];
    if (Array.isArray(locations)) {
        serialized["locations"] = sanitizeValue(locations);
    }

    const extensions = value["extensions"];
    if (isRecord(extensions)) {
        serialized["extensions"] = sanitizeValue(extensions);
    }

    return serialized;
};

const isApolloErrorLike = (value: unknown): value is Record<string, unknown> =>
    isRecord(value) &&
    (value["name"] === "ApolloError" ||
        "graphQLErrors" in value ||
        "protocolErrors" in value ||
        "clientErrors" in value ||
        "networkError" in value);

export const getApolloErrorContext = (error: unknown): ApolloErrorContext | null => {
    if (!isApolloErrorLike(error)) {
        return null;
    }

    const context: ApolloErrorContext = {};
    const name = getStringField(error, "name");
    if (name) {
        context["name"] = name;
    }

    const message = getStringField(error, "message");
    if (message) {
        context["message"] = message;
    }

    const graphQLErrors = getArrayField(error, "graphQLErrors").map((graphQLError) =>
        serializeGraphQLError(graphQLError),
    );
    if (graphQLErrors.length > 0) {
        context["graphQLErrors"] = graphQLErrors;
    }

    const protocolErrors = getArrayField(error, "protocolErrors").map((protocolError) =>
        serializeGraphQLError(protocolError),
    );
    if (protocolErrors.length > 0) {
        context["protocolErrors"] = protocolErrors;
    }

    const clientErrors = getArrayField(error, "clientErrors").map((clientError) => serializeErrorLike(clientError));
    if (clientErrors.length > 0) {
        context["clientErrors"] = clientErrors;
    }

    const networkError = error["networkError"];
    if (networkError) {
        context["networkError"] = serializeErrorLike(networkError);
    }

    if ("extraInfo" in error && error["extraInfo"] !== undefined) {
        context["extraInfo"] = sanitizeValue(error["extraInfo"]);
    }

    return context;
};

export const getSentryCaptureContext = (
    error: unknown,
    tags: SentryTags,
): { tags: SentryTags; contexts?: { apollo: ApolloErrorContext } } => {
    // Last-resort guard: this runs synchronously inside componentDidCatch, so any
    // uncaught throw here would unmount the app. Losing the Apollo context on a
    // pathological error is strictly better than losing the capture (and the page).
    try {
        const apolloContext = getApolloErrorContext(error);

        if (!apolloContext) {
            return { tags };
        }

        const apolloTags: SentryTags = {
            ...tags,
            apolloError: "true",
        };
        if (apolloContext["networkError"]) {
            apolloTags["apolloNetworkError"] = "true";
        }

        return {
            tags: apolloTags,
            contexts: {
                apollo: apolloContext,
            },
        };
    } catch {
        return { tags };
    }
};
